
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsDeveloper } from "@/hooks/useIsDeveloper";
import { shouldShowMockDataForUser, getEnvironment } from "@/utils/environment";
import { AdminOrphanedDataTab } from "./AdminOrphanedDataTab";
import { Code, Database, Shield, Users, Settings } from "lucide-react";

interface AdminTabProps {
  // Add any props needed
}

export const AdminTab = (props: AdminTabProps) => {
  const { data: isDeveloper = false } = useIsDeveloper();
  const currentEnv = getEnvironment();
  const canAccessMockData = shouldShowMockDataForUser(isDeveloper);

  return (
    <div className="space-y-6">
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="general">General Settings</TabsTrigger>
          <TabsTrigger value="app-versioning">App Versioning</TabsTrigger>
          <TabsTrigger value="data-cleanup">Data Cleanup</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Admin Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-3">
                  <h4 className="font-medium">Organization Management</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="user-management">User Management</Label>
                      <span className="text-sm text-muted-foreground">Coming Soon</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="role-management">Role Management</Label>
                      <span className="text-sm text-muted-foreground">Coming Soon</span>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">System Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="integrations">Integration Management</Label>
                      <span className="text-sm text-muted-foreground">Coming Soon</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="billing">Billing & Subscriptions</Label>
                      <span className="text-sm text-muted-foreground">Coming Soon</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {isDeveloper && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Developer Settings
                  <Badge variant="secondary">Platform Developer</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <h4 className="font-medium">Development Tools</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="mock-data-access">Mock Data Access</Label>
                          <p className="text-sm text-muted-foreground">
                            Access to mock data and test features in {currentEnv} environment
                          </p>
                        </div>
                        <Switch 
                          id="mock-data-access" 
                          checked={canAccessMockData} 
                          disabled 
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="debug-mode">Debug Mode</Label>
                          <p className="text-sm text-muted-foreground">
                            Show detailed error messages and debug information
                          </p>
                        </div>
                        <Switch 
                          id="debug-mode" 
                          checked={currentEnv === 'development'} 
                          disabled 
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium">Environment Information</h4>
                    <div className="text-sm space-y-1">
                      <p><strong>Environment:</strong> {currentEnv}</p>
                      <p><strong>Developer Mode:</strong> {isDeveloper ? 'Enabled' : 'Disabled'}</p>
                      <p><strong>Mock Data Access:</strong> {canAccessMockData ? 'Granted' : 'Denied'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="app-versioning" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                App Version Management
                <Badge variant="secondary">v2.0.0-stable</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-3">
                  <h4 className="font-medium">Current Version Status</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">18</div>
                      <div className="text-sm text-green-600">Stable Areas</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">0</div>
                      <div className="text-sm text-blue-600">Working Areas</div>
                    </div>
                    <div className="text-center p-3 bg-orange-50 dark:bg-orange-950 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">0</div>
                      <div className="text-sm text-orange-600">WIP Areas</div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">App Pages Tracked</h4>
                  <div className="text-sm space-y-1">
                    <p><strong>Total Pages:</strong> 18 authenticated app pages</p>
                    <p><strong>All Stable:</strong> Dashboard, Candidates, Jobs, Clients, Analytics, Settings, etc.</p>
                    <p><strong>Landing Page:</strong> Separately versioned (not included here)</p>
                    <p><strong>Baseline:</strong> Current working state captured as v2.0.0-stable</p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">Version Areas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <strong>Core Infrastructure:</strong>
                      <ul className="ml-4 mt-1 space-y-1">
                        <li>• Authentication (stable)</li>
                        <li>• Database (stable)</li>
                        <li>• Components (stable)</li>
                        <li>• Hooks & Utils (stable)</li>
                      </ul>
                    </div>
                    <div>
                      <strong>App Features:</strong>
                      <ul className="ml-4 mt-1 space-y-1">
                        <li>• Candidates System (stable)</li>
                        <li>• Jobs System (stable)</li>
                        <li>• Clients System (stable)</li>
                        <li>• Analytics & Reports (stable)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">Rollback Capability</h4>
                  <div className="text-sm space-y-1">
                    <p><strong>Status:</strong> ✅ Complete rollback capability enabled</p>
                    <p><strong>Baseline:</strong> Current stable state can be restored from any future point</p>
                    <p><strong>Coverage:</strong> All 18 app areas with full dependency tracking</p>
                    <p><strong>Safety:</strong> Emergency rollback available for all areas</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="data-cleanup">
          <AdminOrphanedDataTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};
